import { useEffect, useMemo, useState } from "react";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import { Modal } from "newui/components/ui/Modal";
import { Button } from "newui/components/Button";
import { useAssignmentRemovalMutation } from "newui/features/RolesPermissions/hooks/useUserAssignments";
import type { UserAssignment } from "newui/features/RolesPermissions/types/RolesPermissions.type";
import { ManageRolesTable } from "./ManageRolesTable";
import { DeleteConfirmModal } from "./DeleteConfirmModal";

interface ManageRolesModalProps {
	isOpen: boolean;
	onClose: () => void;
	/** The user whose assignments are listed. `null` renders the empty state. */
	user: UserAssignment | null;
	/**
	 * Rule 6 ("Add unavailable") is computed from the roles call (CONTRACT §5) by
	 * whoever owns the Add flow — this component only reflects it. Re-deriving it
	 * here would mean two places deciding whether Add is offered.
	 */
	canAdd: boolean;
	onAdd: () => void;
}

const DEFAULT_DELETE_ERROR = "These roles could not be removed. Nothing was deleted.";

// Author-supplied copy (review round 1). Kept verbatim, including the absent
// trailing full stop.
export const MANAGE_ROLES_INFO_COPY =
	"Select one or more roles below to remove, or add a new one using the buttons on the right";

interface BulkRevokeFailure {
	assignment_id?: number;
	code?: string;
	message?: string;
}

/**
 * CONTRACT §6 — the bulk revoke is all-or-nothing: a 422 means NOTHING was
 * deleted and `failures[]` names the rows that blocked it. Those server messages
 * are what the admin needs (e.g. "last System Administrator"), so they are shown
 * verbatim; the generic fallback only covers a response that isn't that shape.
 */
export const bulkRevokeFailureMessages = (error: unknown): string[] => {
	const data = (
		error as { response?: { data?: { failures?: unknown; message?: string } } }
	)?.response?.data;

	const failures = Array.isArray(data?.failures) ? (data?.failures as BulkRevokeFailure[]) : [];
	const messages = failures
		.map((failure) => failure?.message)
		.filter((message): message is string => typeof message === "string" && message.length > 0);

	if (messages.length > 0) {
		return messages;
	}

	if (typeof data?.message === "string" && data.message.length > 0) {
		return [data.message];
	}

	return [DEFAULT_DELETE_ERROR];
};

/**
 * PRO-757 (D7) — "Manage roles": the table view of a user's current
 * assignments (Role | Access Level | Item) with checkbox multi-select delete.
 *
 * Nothing here is "unsaved state": the table reflects the server, and the only
 * write is the delete, which applies immediately on confirmation. The footer
 * button just closes.
 */
export const ManageRolesModal = ({
	isOpen,
	onClose,
	user,
	canAdd,
	onAdd,
}: ManageRolesModalProps) => {
	const [checkedIds, setCheckedIds] = useState<number[]>([]);
	const [isConfirmOpen, setIsConfirmOpen] = useState(false);
	const [deleteErrors, setDeleteErrors] = useState<string[]>([]);
	const removalMutation = useAssignmentRemovalMutation();

	const assignments = useMemo(() => user?.assignments ?? [], [user]);

	// Switching subject user must never carry a selection across — the ids would
	// belong to somebody else's assignments.
	useEffect(() => {
		setCheckedIds([]);
		setIsConfirmOpen(false);
		setDeleteErrors([]);
	}, [user?.id]);

	// A refetch can remove rows underneath the selection (another admin revoked
	// one, or our own delete succeeded). Selection is only ever read through this
	// filter so a vanished row can never end up in a delete payload.
	const selectedIds = useMemo(
		() =>
			assignments
				.map((assignment) => assignment.assignment_id)
				.filter((assignmentId) => checkedIds.includes(assignmentId)),
		[assignments, checkedIds]
	);

	const selectedAssignments = useMemo(
		() => assignments.filter((assignment) => selectedIds.includes(assignment.assignment_id)),
		[assignments, selectedIds]
	);

	const toggleRow = (assignmentId: number) => {
		setCheckedIds((current) =>
			current.includes(assignmentId)
				? current.filter((id) => id !== assignmentId)
				: [...current, assignmentId]
		);
	};

	const toggleAll = (selected: boolean) => {
		setCheckedIds(selected ? assignments.map((assignment) => assignment.assignment_id) : []);
	};

	const openConfirm = () => {
		setDeleteErrors([]);
		setIsConfirmOpen(true);
	};

	// Cancelling keeps the checkboxes exactly as they were — the admin is coming
	// back to change the selection, not starting over.
	const closeConfirm = () => {
		setIsConfirmOpen(false);
		setDeleteErrors([]);
	};

	const handleConfirmDelete = () => {
		if (!user || selectedIds.length === 0) {
			return;
		}

		// PRO-730 — negative ids are tour-fixture dummies (see
		// guides/features/Auth/tourFixtures.ts's negative-id convention). The
		// retired per-user role drawer's own tour-dummy test covered this same
		// guarantee for that drawer; ported here since the "Manage a user's
		// roles" guided tour now opens THIS modal (and can reach its real
		// bulk-DELETE) instead. Defence-in-depth alongside Joyride's own
		// `spotlightClicks={false}`.
		if (user.id < 0) {
			return;
		}

		setDeleteErrors([]);
		removalMutation.mutate(
			{ userId: user.id, assignmentIds: selectedIds },
			{
				onSuccess: () => {
					// The mutation invalidates the assignments query; the table re-renders
					// from the server's answer rather than a locally-spliced copy.
					setCheckedIds([]);
					setIsConfirmOpen(false);
				},
				// No invalidation on failure: the batch is atomic, so the table on screen
				// is still correct and re-fetching would only make the rows flicker.
				onError: (error: unknown) => setDeleteErrors(bulkRevokeFailureMessages(error)),
			}
		);
	};

	const handleClose = () => {
		setCheckedIds([]);
		setIsConfirmOpen(false);
		setDeleteErrors([]);
		onClose();
	};

	const headerActions = (
		<div className="flex w-full items-center justify-end gap-2">
			<Button
				// The app's destructive treatment (solid red), the same `btntype` every
				// other delete/cancel-for-good action uses — and the same one the confirm
				// dialog's "Confirm delete" carries, so the two read as one action.
				btntype="danger"
				size="sm"
				disabled={selectedIds.length === 0}
				onClick={openConfirm}
				data-testid="manage-roles-delete"
			>
				<FiTrash2 className="mr-1.5 inline" size={14} />
				Delete
			</Button>
			<Button
				btntype="primary"
				size="sm"
				disabled={!canAdd}
				onClick={onAdd}
				data-testid="manage-roles-add"
			>
				<FiPlus className="mr-1.5 inline" size={14} />
				Add
			</Button>
		</div>
	);

	const footer = (
		<div className="flex w-full justify-end">
			<Button btntype="primary" size="sm" onClick={handleClose} data-testid="manage-roles-close">
				Close
			</Button>
		</div>
	);

	return (
		<>
			<Modal
				isOpen={isOpen}
				onClose={handleClose}
				title={user ? `Manage roles — ${user.name}` : "Manage roles"}
				size="xxl"
				modalType="standard"
				iconType="edit"
				// F8 — Escape mirrors this dialog's own Close. Nothing here is unsaved:
				// deletes apply immediately, so the only casualty is a row selection.
				// While the confirm layer is open Chakra routes Escape to IT, not here,
				// so the outer modal cannot be pulled out from under it.
				closeOnEsc
				headerAccessory={headerActions}
				customFooter={footer}
			>
				<div data-testid="manage-roles-modal">
					{/*
						Author review (2026-09-23): the table alone did not say what the
						two header buttons were for. Same info treatment the Add flow's
						notices use — matched deliberately rather than imported, since that
						component carries the Add flow's own frozen A6 copy.
					*/}
					<p
						className="mb-3 rounded-md border border-blue-200 bg-blue-50 px-3.5 py-2.5 text-sm leading-relaxed text-blue-900"
						data-testid="manage-roles-info"
						role="note"
					>
						{MANAGE_ROLES_INFO_COPY}
					</p>
					<ManageRolesTable
						assignments={assignments}
						selectedIds={selectedIds}
						onToggleRow={toggleRow}
						onToggleAll={toggleAll}
					/>
				</div>
			</Modal>
			<DeleteConfirmModal
				isOpen={isConfirmOpen}
				assignments={selectedAssignments}
				isDeleting={removalMutation.isPending}
				errorMessages={deleteErrors}
				onCancel={closeConfirm}
				onConfirm={handleConfirmDelete}
			/>
		</>
	);
};
